import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { NotebookPen } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { catStyle } from "@/lib/constants";
import { fmtDeadline, toDay, toLocal, weekStartOf } from "@/lib/time";
import ScheduleCardDialog from "@/components/jobs/ScheduleCardDialog";
import JobCardDialog from "@/components/jobs/JobCardDialog";
import { StatusPill } from "@/components/jobs/StatusBox";

const stage = (c) => {
  const done = c.tasks.filter((t) => t.status === "done").length;
  if (c.status === "Complete" || (c.tasks.length && done === c.tasks.length)) return "done";
  const started = done || c.tasks.some((t) => t.in_planner || t.subtasks?.some((s) => s.done));
  return started || (c.status && c.status !== "Not started") ? "progress" : "new";
};
const COLUMNS = [["new", "Not started"], ["progress", "In progress"], ["done", "Done"]];

function CardTile({ c, onOpen }) {
  const s = catStyle(c.category);
  const done = c.tasks.filter((t) => t.status === "done").length;
  const overdue = stage(c) !== "done" && c.deadline < toDay(new Date());
  return (
    <li className="fade-up">
      <button onClick={() => onOpen(c.id)} data-testid={`job-card-item-${c.id}`}
        className="w-full text-left rounded-2xl border border-[#103080]/10 bg-white p-4 hover:-translate-y-[1px] hover:shadow-md transition-[transform,box-shadow] duration-200">
        <div className="flex items-center justify-between gap-2">
          <span className="rounded-full px-2.5 py-0.5 text-xs font-semibold" style={{ background: s.bg, color: s.text }}>{c.category}</span>
          <StatusPill status={c.status} testId={`job-card-status-${c.id}`} />
        </div>
        <p className="font-semibold text-[#103080] mt-3">{c.client_name}</p>
        <p className="text-sm text-[#0F172A]/70">{c.title}</p>
        <p className="text-xs text-[#0F172A]/50 mt-1">{c.period_label}{c.processes && <span className="ml-2 inline-flex items-center gap-0.5 text-[#0090D0]"><NotebookPen size={10} />Processes</span>}</p>
        <div className="mt-3 flex items-center justify-between text-xs">
          <span className={overdue ? "text-red-600 font-semibold" : "text-[#0F172A]/60"}>Due {fmtDeadline(c.deadline)}{c.statutory_deadline && c.statutory_deadline !== c.deadline && <span className="text-[#0F172A]/40" data-testid={`job-card-statutory-${c.id}`}> · statutory {fmtDeadline(c.statutory_deadline)}</span>}</span>
          <span className="font-semibold text-[#0090D0]" data-testid={`job-card-progress-${c.id}`}>{done}/{c.tasks.length} tasks</span>
        </div>
        <div className="mt-2 h-1.5 rounded-full bg-[#F5F2EB] overflow-hidden">
          <div className="h-full bg-[#0090D0] transition-[width] duration-300" style={{ width: `${c.tasks.length ? (done / c.tasks.length) * 100 : 0}%` }} />
        </div>
      </button>
    </li>
  );
}

export default function JobCards() {
  const { user } = useAuth();
  const [scope, setScope] = useState("mine");
  const [cards, setCards] = useState([]);
  const [directory, setDirectory] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [scheduling, setScheduling] = useState(null);
  const people = Object.fromEntries(directory.map((p) => [p.id, p.name]));

  const load = useCallback(async () => {
    try {
      setCards((await api.get("/job-cards", { params: { scope } })).data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [scope]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    api.get("/directory").then((r) => setDirectory(r.data)).catch(() => {});
  }, []);

  const replanFor = (t) => api.post("/schedule/auto", { week_start: toDay(weekStartOf(new Date())), now: toLocal(new Date()), user_id: t.user_id === user.id ? null : t.user_id });

  const onPatch = async (t, body, msg) => {
    try {
      await api.patch(`/tasks/${t.id}`, body);
      await replanFor(t);
      if (msg) toast.success(msg);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onSubtasks = async (t, subtasks) => {
    setCards((p) => p.map((c) => ({ ...c, tasks: c.tasks.map((x) => (x.id === t.id ? { ...x, subtasks } : x)) })));
    try {
      await api.patch(`/tasks/${t.id}`, { subtasks });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
      load();
    }
  };

  const open = cards.find((c) => c.id === openId);
  return (
    <div className="space-y-8 fade-up" data-testid="job-cards-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-[#0090D0] font-semibold mb-3">Job cards</p>
          <h1 className="font-serif-brand font-semibold text-[#103080] text-4xl sm:text-5xl">What's <i className="text-[#0090D0]">coming up</i></h1>
          <p className="mt-3 text-[#0F172A]/60 max-w-xl">Open a card to work through its tasks, tick off subtasks and add tasks to the planner of the person they're assigned to.</p>
        </div>
        {user.role === "admin" && <div className="inline-flex rounded-full bg-[#F5F2EB] p-1 shrink-0">
          {[["mine", "My cards"], ["all", "Whole team"]].map(([k, label]) => (
            <button key={k} onClick={() => setScope(k)} data-testid={`job-cards-scope-${k}`}
              className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors duration-200 ${scope === k ? "bg-white text-[#103080] shadow-sm" : "text-[#103080]/60"}`}>{label}</button>
          ))}
        </div>}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {COLUMNS.map(([key, title]) => {
          const list = cards.filter((c) => stage(c) === key);
          return (
            <section key={key} className="rounded-2xl bg-[#F5F2EB] border border-[#103080]/10 p-4" data-testid={`job-cards-column-${key}`}>
              <h2 className="font-serif-brand font-semibold text-lg text-[#103080] px-1 mb-3">{title} <span className="text-sm text-[#0F172A]/40 font-sans">{list.length}</span></h2>
              {list.length === 0 ? <p className="text-sm text-[#0F172A]/40 px-1 py-6 text-center">Nothing here.</p> : (
                <ul className="space-y-3 max-h-[75vh] overflow-y-auto thin-scroll pr-1">
                  {list.map((c) => <CardTile key={c.id} c={c} onOpen={setOpenId} />)}
                </ul>
              )}
            </section>
          );
        })}
      </div>
      <JobCardDialog card={open} user={user} people={people} directory={directory} onClose={() => setOpenId(null)} onReload={load}
        onSchedule={setScheduling} onPatch={onPatch} onSubtasks={onSubtasks} />
      <ScheduleCardDialog card={scheduling} assigneeName={people[scheduling?.user_id]} isMine={scheduling?.user_id === user.id}
        onClose={() => setScheduling(null)} onSaved={async (t) => { await replanFor(t).catch(() => {}); load(); }} />
    </div>
  );
}
